import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Sidebar from "../Components/Sidebars/main";
import NavBar from "../Components/Navbars/main";
import { setHasNoData } from "../Redux/Directory/directorySlice";
import Footer from "../Components/common/footer";
import { fetchStates } from "../Redux/states/statesSlice";
// import LitigationCentralDashboard from "../Components/LitigationCentralDashboard/main";
// import NotesSectionDashboard from "../Components/NotesSectionDashboard/main";
import {
  fetchPageObject,
  fetchShakespeareStatus,
  getCaseId,
  getClientId,
  setCaseId,
  setClientId,
} from "../Utils/helper";
import ActionBarComponent from "../Components/common/ActionBarComponent";
import NewChangesTabs from "../Components/new-client-dashboard/NewChangesTabs";
import DependentDatesNeededTab from "../Components/LitigationCentralDashboard/tabs/dependentDatesNeededTab";
import SolsTab from "../Components/LitigationCentralDashboard/tabs/solsTab";
import TrialsTab from "../Components/LitigationCentralDashboard/tabs/trialsTab";
import DefendantsUnservedTab from "../Components/LitigationCentralDashboard/tabs/defendantsUnservedTab";
import { api_without_cancellation } from "../api/api";
import SOLIcon from "../assets/litigation/sol.svg";
import { ClientDataContext } from "../Components/ClientDashboard/shared/DataContext";
import EditLitigationActPopup from "../Components/LitigationDashboard/Modals/EditLitigationActPopup";
import EditSOLPopup from "../Components/LitigationDashboard/Modals/EditSOLPopup";
import { setBlurEffect } from "../Redux/header_blur/action";
import { setHeaderName } from "../Redux/header_name/action";

const EMPTY_LITIGATION_LISTS = {
  dependant_date_missing: [],
  sols: [],
  trials: [],
  defendants_unserved: [],
};

const PageContent = ({
  currentPage,
  eventTypesData,
  fetchLitigationActs,
  litigationLists,
  onActClick,
}) => {
  switch (currentPage) {
    case "dependant-dates-needed":
      return (
        <DependentDatesNeededTab
          data={litigationLists.dependant_date_missing}
          onActClick={onActClick}
          eventTypesData={eventTypesData}
          fetchLitigationActs={fetchLitigationActs}
        />
      );
    case "sols":
      return (
        <SolsTab
          data={litigationLists.sols}
          onActClick={onActClick}
          eventTypesData={eventTypesData}
          fetchLitigationActs={fetchLitigationActs}
        />
      );
    case "trials":
      return (
        <TrialsTab
          data={litigationLists.trials}
          onActClick={onActClick}
          eventTypesData={eventTypesData}
          fetchLitigationActs={fetchLitigationActs}
        />
      );
    case "defendants-unserved":
      return (
        <DefendantsUnservedTab
          defendants={litigationLists.defendants_unserved}
          fetchLitigationActs={fetchLitigationActs}
        />
      );
    default:
      return null;
  }
};

function NewLitigationCentralPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLitigationActDataUpdated, setLitigationActDataUpdated } = useContext(ClientDataContext);
  const caseId = getCaseId();
  const clientId = getClientId();
  const [pageObject, setPageObject] = useState(null);
  const [activeTab, setActiveTab] = useState("dependant-dates-needed");
  const [eventTypeId, setEventTypeId] = useState(null);
  const [eventTypeName, setEventTypeName] = useState("");
  const [eventTypesData, setEventTypesData] = useState([]);
  const [isEventTypeOpen, setIsEventTypeOpen] = useState(false);
  const [litigationLists, setLitigationLists] = useState(EMPTY_LITIGATION_LISTS);
  const [searchQuery, setSearchQuery] = useState("");
  const eventTypeDropdownRef = useRef(null);
  const [selectedAct, setSelectedAct] = useState(null);
  const [isOpeningCase, setIsOpeningCase] = useState(false);
  const caseSwitchPendingRef = useRef(false);
  const selectedCase = selectedAct?.for_case;
  const selectedCaseId = selectedCase?.id ?? selectedCase;
  const selectedClient = selectedCase?.for_client ?? selectedAct?.for_client;
  const selectedClientId = selectedClient?.id ?? selectedClient;
  const ActPopup = selectedAct?.event_type_id ? EditLitigationActPopup : EditSOLPopup;

  const handleActClick = (act, event) => {
    if (event.target.closest("button, input, select, textarea, a, label, [role='button']")) return;
    setSelectedAct(act);
  };

  const handleGoToCase = async () => {
    if (!selectedClientId || !selectedCaseId || caseSwitchPendingRef.current) return;

    caseSwitchPendingRef.current = true;
    setIsOpeningCase(true);

    try {
      await api_without_cancellation.get(
        `/api/switch_client/${selectedClientId}/${selectedCaseId}/1/`
      );
      setClientId(selectedClientId);
      setCaseId(selectedCaseId);
      dispatch(setBlurEffect(false));
      dispatch(setHeaderName("Case"));
      setSelectedAct(null);
      navigate(`/bp-client/${selectedClientId}/${selectedCaseId}`, {
        state: { openingCase: selectedCase },
      });
    } catch (error) {
      console.error("Unable to open the litigation act's case:", error);
      toast.error("Unable to open this case. Please try again.");
    } finally {
      caseSwitchPendingRef.current = false;
      setIsOpeningCase(false);
    }
  };

  const fetchLitigationActs = useCallback(async () => {
    try {
      const params = {
        case_id: caseId,
        client_id: clientId,
        search: searchQuery,
      };

      if (eventTypeId) params.event_type_id = eventTypeId;

      const response = await api_without_cancellation.get(
        "/api/litigation-page/litigation-act-for-litigation-central/",
        { params }
      );
      const responseData = response.data || {};
      const data = responseData.data ?? responseData;

      setLitigationLists({
        dependant_date_missing: Array.isArray(data)
          ? data
          : Array.isArray(data?.dependant_date_missing)
            ? data.dependant_date_missing
            : [],
        sols: Array.isArray(data?.sols) ? data.sols : [],
        trials: Array.isArray(data?.trials) ? data.trials : [],
        defendants_unserved: Array.isArray(data?.defendants_unserved)
          ? data.defendants_unserved
          : [],
      });
    } catch (error) {
      console.error("Error fetching litigation central data:", error);
      setLitigationLists(EMPTY_LITIGATION_LISTS);
    }
  }, [caseId, clientId, eventTypeId, searchQuery]);

  useEffect(() => {
    if (isLitigationActDataUpdated) {
      fetchLitigationActs();
      setLitigationActDataUpdated(false);
    }
  }, [fetchLitigationActs, isLitigationActDataUpdated, setLitigationActDataUpdated]);

  useEffect(() => {
    if (caseId && !pageObject) {
      fetchPageObject(caseId, 19).then((data) => {
        setPageObject(data);
      });
    }
  }, [caseId, pageObject]);
  useEffect(() => {
    dispatch(setHasNoData(false));
    dispatch(fetchStates());
    // sending page ids directly now.
    fetchShakespeareStatus(getCaseId(), 19, dispatch);
  }, [dispatch]);

  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const response = await api_without_cancellation.get("/api/all/event/types/");
        setEventTypesData(response.data.data || []);
      } catch (error) {
        console.error("Error fetching Event Types:", error);
      }
    };

    fetchEventTypes();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(fetchLitigationActs, 300);
    return () => window.clearTimeout(timeoutId);
  }, [fetchLitigationActs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (eventTypeDropdownRef.current && !eventTypeDropdownRef.current.contains(event.target)) {
        setIsEventTypeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const [sideBarCollapsed, setSideBarCollapsed] = useState(null);

  useEffect(() => {
    const getUserPreferencesFromStorage = () => {
      const preferences = localStorage.getItem("userPreferences");
      const preferencesObj = preferences ? JSON.parse(preferences) : null;
      setSideBarCollapsed(preferencesObj?.SidebarView);
    };
    getUserPreferencesFromStorage();
  }, []);

  const tabsData = [
    {
      id: "dependant-dates-needed",
      label: "Dependant Dates Needed",
      onClick: () => handleTabChange("dependant-dates-needed"),
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-5)",
      activeColor: "white",
    },
    {
      id: "sols",
      label: "SOLs",
      onClick: () => handleTabChange("sols"),
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-5)",
      activeColor: "white",
    },
    {
      id: "trials",
      label: "Trials",
      onClick: () => handleTabChange("trials"),
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-5)",
      activeColor: "white",
    },
    {
      id: "defendants-unserved",
      label: "Defendants Unserved",
      onClick: () => {
        handleTabChange("defendants-unserved");
      },
      className: "tab-trapezium-cycles-corners",
      background: "var(--primary-5)",
      activeColor: "white",
    },
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <>
      <div className={`page-wrapper ${sideBarCollapsed ? "force-collapsed" : ""}`}>
        <Sidebar />
        <div className="page-container overflow-hidden">
          <NavBar flaggedPageName={"19"} />
          <ActionBarComponent
            // src={MedicalTreatmentIcon}
            // page_name={pageObject?.name}
            page_name={"Litigation Central"}
            pageObj={pageObject}
            buttons={[]}
            isChecklist={false}
          />
          <div className="TreatmentPage-main-container invisible-scrollbar">
            <div className="d-flex align-items-center">
              <div style={{ width: "50%" }}>
                <NewChangesTabs
                  currentTab={activeTab}
                  tabsData={tabsData}
                  height={25}
                  labelClass=" justify-content-start"
                />
              </div>
            </div>

            <div className="d-flex align-items-center m-b-5 m-t-5 w-100">
              <div
                className={
                  activeTab === "dependant-dates-needed" ? "w-100 p-l-0" : "w-100 p-l-0 p-r-5"
                }
              >
                <input
                  type="text"
                  placeholder="Search litigation acts..."
                  className="form-control height-25"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </div>

              {activeTab === "dependant-dates-needed" && (
                <div className="p-r-5 p-l-5">
                  <div
                    ref={eventTypeDropdownRef}
                    className="height-25 p-l-0 m-r-0 pe-0 custom-select-new-provider"
                    style={{ position: "relative" }}
                  >
                    <div
                      className="dropdown-button form-control pt-0 pb-0 pe-0 height-25 d-flex align-items-center"
                      onClick={() => setIsEventTypeOpen((isOpen) => !isOpen)}
                      style={{
                        paddingLeft: eventTypeId ? "0px" : "5px",
                        color: "black",
                        minWidth: "200px",
                      }}
                    >
                      <span id="selectedOption">
                        {eventTypeId ? (
                          <div
                            className="d-flex p-l-5 align-items-center justify-content-start"
                            style={{
                              height: "25px",
                              fontSize: "16px",
                              color: "black",
                              fontWeight: "700",
                              gap: "5px",
                            }}
                          >
                            {eventTypeName && (
                              <div className="d-flex align-items-center">
                                {eventTypeName === "Statute of Limitations" ? (
                                  <img
                                    src={SOLIcon}
                                    alt=""
                                    className="d-flex align-items-center justify-content-center ic-15"
                                  />
                                ) : (
                                  <i
                                    className={`ic ic-15 ${
                                      eventTypeName === "Discovery"
                                        ? "ic-discovery"
                                        : eventTypeName === "Statute of Limitations" ||
                                            eventTypeName === "Statute of Limitation"
                                          ? "ic-SOL"
                                          : eventTypeName === "Expert Discovery"
                                            ? "ic-discovery"
                                            : "ic-" + eventTypeName
                                    }`}
                                  />
                                )}
                              </div>
                            )}
                            <h5>{eventTypeName}</h5>
                          </div>
                        ) : (
                          "Select Event Type"
                        )}
                      </span>
                    </div>

                    {isEventTypeOpen && (
                      <div
                        className="dropdown-menu p-0 mt-0"
                        style={{
                          width: "100%",
                          border: "1px solid #ced4da",
                          scrollbarWidth: "none",
                          maxHeight: "450px",
                          fontSize: "16px",
                          overflowY: "auto",
                        }}
                      >
                        {eventTypesData.map((eventType, index) => (
                          <div
                            key={eventType.id}
                            className="dropdown-option d-flex align-items-center height-25"
                            style={{
                              color: "black",
                              fontWeight: "400",
                              fontSize: "16px",
                              cursor: "pointer",
                              paddingLeft: "5px",
                              backgroundColor:
                                index % 2 === 0 ? "var(--primary-5)" : "var(--primary-10)",
                            }}
                            onClick={() => {
                              setEventTypeId(eventType.id);
                              setEventTypeName(eventType.litigation_event_type);
                              setIsEventTypeOpen(false);
                            }}
                          >
                            {eventType.litigation_event_type === "Statute of Limitations" ? (
                              <img
                                src={SOLIcon}
                                alt=""
                                className="d-flex align-items-center justify-content-center m-r-5 ic-15"
                              />
                            ) : (
                              <i
                                className={`ic ic-15 ${
                                  eventType.litigation_event_type === "Discovery"
                                    ? "ic-discovery"
                                    : eventType.litigation_event_type === "Statute of Limitation"
                                      ? "ic-SOL"
                                      : eventType.litigation_event_type === "Expert Discovery"
                                        ? "ic-discovery"
                                        : "ic-" + eventType.litigation_event_type
                                } m-r-5`}
                              />
                            )}
                            <h5 className="">{eventType.litigation_event_type}</h5>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <PageContent
              currentPage={activeTab}
              eventTypesData={eventTypesData}
              fetchLitigationActs={fetchLitigationActs}
              litigationLists={litigationLists}
              onActClick={handleActClick}
            />

            {/* <NotesSectionDashboard pageName={"Litigation"} /> */}
          </div>
        </div>
      </div>
      <Footer />
      {selectedAct && (
        <ActPopup
          key={selectedAct.id}
          showPopup
          handleClose={() => setSelectedAct(null)}
          litigationDetail={selectedAct.litigation}
          act={selectedAct}
          eventTypesData={eventTypesData}
          clientId={selectedClientId}
          caseId={selectedCaseId}
          onGoToCase={handleGoToCase}
          isOpeningCase={isOpeningCase}
        />
      )}
    </>
  );
}

export default NewLitigationCentralPage;
